import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import * as S from './RelationshipForm.style'

import { noop } from 'lodash'
import { createOption, Option } from 'helpers/selects'
import { DeleteData } from 'tabs/data/components/DeleteData'
import { CButton } from 'tabs/data/components/CButton'
import { Prompt } from 'react-router-dom'
import { CSelect } from 'tabs/data/components/CSelect'
import { RelationshipsRequest, RelationshipType, TableRelationshipFilterDirection } from '../relationships.types'
import { DIRECTION_TYPE_OPTIONS, RELATIONSHIP_TYPE_OPTIONS } from './RelationshipForm.constants'
import { MenuPosition } from 'react-select'
import { Column } from '../../../../../api/generated'

type RelationshipFormProps = {
  sourceTable: string
  destinationTable: string
  sourceColumn: string
  targetColumn: string
  type?: RelationshipType
  direction?: TableRelationshipFilterDirection

  cancelButtonLabel?: string
  submitButtonLabel?: string
  selectMenuPosition?: MenuPosition

  options: Option[]
  joinedTables: string[]
  synonyms: string[]
  onSubmit: (request: RelationshipsRequest) => Promise<void>
  onCancel?: () => void
  onDelete?: (forceDelete: boolean) => Promise<any>
  columns: Map<string, Column[]>
}

export const RelationshipForm: FC<RelationshipFormProps> = ({
  sourceTable: sourceTableValues,
  destinationTable: destinationTableValues,

  sourceColumn: initiliasedColumn,
  targetColumn,

  type: typeValue,
  direction: directionValue,

  cancelButtonLabel = 'Cancel',
  submitButtonLabel = 'Submit',
  options,
  selectMenuPosition,
  onSubmit,
  onCancel = noop,
  onDelete,
  columns,
}) => {
  const [type, setType] = useState<RelationshipType | null>(typeValue || null)
  const [direction, setDirection] = useState<TableRelationshipFilterDirection | null>(directionValue || null)
  const [loading, setLoading] = useState(false)
  const [sourceTable, setSourceTable] = useState<string>(sourceTableValues || '')
  const [destinationTable, setDestinationTable] = useState<string>(destinationTableValues || '')

  const [sourceColumn, setSourceColumn] = useState<string>(initiliasedColumn)
  const [destinationColumn, setDestinationColumn] = useState<string>(targetColumn)

  const [errorText, setErrorText] = useState('')

  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!destinationColumn || !sourceColumn) {
      setErrorText('Column cannot be empty')
    } else {
      setErrorText('')
    }
  }, [destinationColumn, sourceColumn])

  const formHasChanges =
    (sourceTable || []).toString() !== (sourceTableValues || []).toString() ||
    (destinationTable || []).toString() !== (destinationTableValues || []).toString() ||
    (sourceColumn || []).toString() !== (initiliasedColumn || []).toString() ||
    (destinationColumn || []).toString() !== (targetColumn || []).toString() ||
    (direction || []).toString() !== (directionValue || []).toString() ||
    (type || []).toString() !== (typeValue || []).toString()

  const isSubmitButtonDisabled = loading || errorText

  const srcOptions = useMemo(() => {
    if (!sourceTable.trim().length) {
      return options
    }

    const isOptionsContainsTable = options.some(({ value }) => value === sourceTable)
    return isOptionsContainsTable ? options : [...options, createOption(sourceTable)]
  }, [options, sourceTable])

  const destOptions = useMemo(() => {
    if (!destinationTable.trim().length) {
      return options
    }

    const isOptionsContainsTable = options.some(({ value }) => value === destinationTable)
    return isOptionsContainsTable ? options : [...options, createOption(destinationTable)]
  }, [options, destinationTable])

  const handleReset = () => {
    setSourceTable(sourceTableValues || '')
    setDestinationTable(destinationTableValues || '')
    setSourceColumn(initiliasedColumn || '')
    setDestinationColumn(targetColumn || '')
    setDirection(directionValue)
    setType(typeValue)

    if (onCancel) {
      onCancel()
    }
  }

  const getColumnOptions = (source: string): Option[] => {
    const tableColumns = columns.get(source)
    if (tableColumns) {
      return tableColumns.map(c => createOption(c.columnName, c.columnName))
    } else {
      return []
    }
  }

  const handleSubmit = async () => {
    if (loading) return

    try {
      setLoading(true)
      const request: RelationshipsRequest = {
        relationshipSourceTableId: sourceTable,
        relationshipDestinationTableId: destinationTable,
        relationshipType: type,
        filterDirection: direction,
        sourceColumnName: sourceColumn,
        targetColumnName: destinationColumn,
      }
      await onSubmit(request)
    } catch (e) {
      throw e
    } finally {
      if (isMounted.current) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (formHasChanges) window.onbeforeunload = () => true
    if (!formHasChanges) window.onbeforeunload = () => null

    return () => {
      window.onbeforeunload = null
    }
  }, [formHasChanges, isSubmitButtonDisabled])

  return (
    <S.Container>
      <Prompt
        when={formHasChanges}
        message="Are you sure you want to leave this page without saving your current changes?"
      />
      <S.Form>
        <S.Row>
          <S.RowCell>
            <S.Title>Source table</S.Title>
            <CSelect
              disabled={loading}
              options={srcOptions}
              value={sourceTable}
              menuPosition={selectMenuPosition}
              onChange={v => {
                setSourceTable(v)
                setSourceColumn('')
              }}
            />
          </S.RowCell>

          <S.RowCell>
            <S.Title>Column</S.Title>
            <CSelect
              disabled={loading}
              options={getColumnOptions(sourceTable)}
              value={sourceColumn}
              menuPosition={selectMenuPosition}
              onChange={v => setSourceColumn(v)}
            />
            <S.Error>{errorText}</S.Error>
          </S.RowCell>
        </S.Row>

        <S.Row>
          <S.RowCell>
            <S.Title>Cardinality</S.Title>
            <CSelect
              disabled={loading}
              options={RELATIONSHIP_TYPE_OPTIONS}
              value={type}
              menuPosition={selectMenuPosition}
              onChange={v => setType(v as RelationshipType)}
            />
          </S.RowCell>

          <S.RowCell />
        </S.Row>

        <S.Row>
          <S.RowCell>
            <S.Title>Destination table</S.Title>
            <CSelect
              disabled={loading}
              options={destOptions}
              value={destinationTableValues}
              menuPosition={selectMenuPosition}
              onChange={v => {
                setDestinationTable(v)
                setDestinationColumn('')
              }}
            />
          </S.RowCell>

          <S.RowCell>
            <S.Title>Column</S.Title>
            <CSelect
              disabled={loading}
              options={getColumnOptions(destinationTable)}
              value={destinationColumn}
              menuPosition={selectMenuPosition}
              onChange={v => setDestinationColumn(v)}
            />
            <S.Error>{errorText}</S.Error>
          </S.RowCell>
        </S.Row>

        <S.Row>
          <S.RowCell>
            <S.Title>Cross-filter direction</S.Title>
            <CSelect
              disabled={loading}
              options={DIRECTION_TYPE_OPTIONS}
              value={direction}
              menuPosition={selectMenuPosition}
              onChange={v => setDirection(v as TableRelationshipFilterDirection)}
            />
          </S.RowCell>

          <S.RowCell />
        </S.Row>

        <S.Footer>
          <CButton label={cancelButtonLabel} color="secondary" onClick={handleReset} />
          <CButton
            label={submitButtonLabel}
            color="primary"
            disabled={Boolean(!formHasChanges || isSubmitButtonDisabled)}
            onClick={handleSubmit}
          />

          {onDelete && <DeleteData buttonLabel="Delete Relation" onDelete={onDelete} />}
        </S.Footer>
      </S.Form>
    </S.Container>
  )
}
